/* Входы формы «Карточки для маркетплейса» ⇄ состояние движка шаблона (план вехи 3, шаг 2; mcard-infog K-IN). Чистые функции без DOM:
   viewOf — что показать в S по входам сервиса (повтор из «Истории», заявка родной формы), inputsOf — входы из S (зеркало inputs-mirror,
   точки записи — syncFoot движка). Ключи и кодирование — те же, что пишет родная форма (MCardOrderForm.tsx):
   - «Что сделать» (outputs) — только опубликованное сочетание работ (жетоны формы photos|slides|video|description, у шаблона —
     photo|slides|video|desc); неопубликованное сочетание и «ничего не отмечено» не пишутся (во входах остаётся прежнее, кнопку
     закрывает готовность экрана);
   - одно поле «Название и преимущества» (text) или пара name + advantages;
   - сколько фото, площадка, пожелания и варианты работ (набор сцен, оформление, движение, стиль текста) — только опубликованные значения.
   Слоты фото пишет мост загрузок (media-bridge): inputsOf их не трогает. */
import type { StudioForm } from '../studio/studio-api';
import { enumOf, joinProductText, photoSlots, splitProductText, textOf, type ControlKey, type Field } from '../slides/product-model';

/** Работы шаблона (OPS) по порядку */
export type McardWork = 'photo' | 'slides' | 'video' | 'desc';
export const WORKS: readonly McardWork[] = ['photo', 'slides', 'video', 'desc'];
/** Жетоны работы в значениях поля «Что сделать» (первый — родной формы) */
const TOKENS: Record<McardWork, readonly string[]> = { photo: ['photos', 'photo'], slides: ['slides'], video: ['video'], desc: ['description', 'desc'] };
/** Поле варианта работы */
export const VARIANT: Record<McardWork, ControlKey> = { photo: 'photoSet', slides: 'slidesStyle', video: 'videoMotion', desc: 'descTone' };
/** Работа по жетону формы (или по виду секции итога) */
export const workOf = (token: string): McardWork | undefined => WORKS.find(work => TOKENS[work].includes(token.trim()));

export interface McardFields {
  outputs?: Field;
  count?: Field;
  text?: Field;
  name?: Field;
  advantages?: Field;
  platform?: Field;
  wishes?: Field;
  variants: Partial<Record<McardWork, Field>>;
  photos: Field[];
}
/** Поля формы экрана (как MCardOrderForm: одно поле text, без него — пара name + advantages) */
export function mcardFields(form: StudioForm | null | undefined): McardFields {
  const text = textOf(form, 'text');
  const variants: Partial<Record<McardWork, Field>> = {};
  for (const work of WORKS) {
    const field = enumOf(form, VARIANT[work]);
    if (field) variants[work] = field;
  }
  return {
    outputs: enumOf(form, 'outputs'),
    count: enumOf(form, 'photoCount'),
    text,
    name: text ? undefined : textOf(form, 'name'),
    advantages: text ? undefined : textOf(form, 'advantages'),
    platform: enumOf(form, 'platform'),
    wishes: textOf(form, 'wishes'),
    variants,
    photos: form ? photoSlots(form) : [],
  };
}

/** Работы значения «Что сделать» (неизвестный жетон делает значение чужим: null) */
export function worksOf(value: string | undefined): Set<McardWork> | null {
  if (!value) return null;
  const works = new Set<McardWork>();
  for (const token of value.split(',')) {
    const work = workOf(token);
    if (!work) return null;
    works.add(work);
  }
  return works.size ? works : null;
}
/** Опубликованное значение «Что сделать» для отмеченных работ (порядок жетонов — формы); нет такого сочетания — undefined */
export function comboOf(field: Field | undefined, pick: Partial<Record<McardWork, boolean>>): string | undefined {
  if (!field) return undefined;
  const want = WORKS.filter(work => pick[work]);
  if (!want.length) return undefined;
  return field.values.find(value => {
    const works = worksOf(value);
    return !!works && works.size === want.length && want.every(work => works.has(work));
  });
}
/** Значение «Что сделать» одной работы (её цена в prices поля) */
export const soloOf = (field: Field | undefined, work: McardWork) => comboOf(field, { [work]: true });
/** Работы, которые есть хотя бы в одном опубликованном сочетании */
export const worksPublished = (field: Field | undefined): McardWork[] =>
  field ? WORKS.filter(work => field.values.some(value => worksOf(value)?.has(work))) : WORKS.slice();

/** Что экран держит в S: поле «Название и преимущества» как есть, отметки работ, сколько фото, площадка, пожелания, варианты */
export interface McardView {
  text: string;
  pick?: Record<McardWork, boolean>;
  nph?: number;
  plat?: string;
  wish?: string;
  vars: Partial<Record<McardWork, string | number>>;
}
/** Значение варианта для S: у движения — число (как varPick шаблона), у остальных — строка */
const varValue = (work: McardWork, value: string): string | number => (work === 'video' && /^\d+$/.test(value) ? Number(value) : value);
const pickOf = (works: Set<McardWork>) => Object.fromEntries(WORKS.map(work => [work, works.has(work)])) as Record<McardWork, boolean>;

/** Входы → вид S (только опубликованные поля и значения; неопубликованное не задано) */
export function viewOf(form: StudioForm | null | undefined, inputs: Record<string, string>): McardView {
  const f = mcardFields(form);
  const text = f.text
    ? (inputs[f.text.key] ?? '')
    : f.name
      ? joinProductText(inputs[f.name.key] ?? '', (f.advantages ? (inputs[f.advantages.key] ?? '') : '').split('\n'))
      : '';
  const view: McardView = { text, vars: {} };
  const pick = (field: Field | undefined) => {
    const value = field ? inputs[field.key] : undefined;
    return value !== undefined && field!.values.includes(value) ? value : undefined;
  };
  const works = worksOf(pick(f.outputs));
  if (works) view.pick = pickOf(works);
  const n = Number(pick(f.count));
  if (Number.isSafeInteger(n) && n > 0) view.nph = n;
  const plat = pick(f.platform);
  if (plat) view.plat = plat;
  if (f.wishes) view.wish = inputs[f.wishes.key] ?? '';
  for (const work of WORKS) {
    const value = pick(f.variants[work]);
    if (value) view.vars[work] = varValue(work, value);
  }
  return view;
}

/** Вид S → входы поверх prev: опубликованные поля экрана; слоты фото — как в prev (их пишет мост). Пустое поле, которого в prev
    не было, не появляется (как у родной формы: ключ пишет только правка) */
export function inputsOf(form: StudioForm | null | undefined, view: McardView, prev: Record<string, string>): Record<string, string> {
  const f = mcardFields(form);
  const next = { ...prev };
  const put = (field: Field | undefined, value: string | undefined) => {
    if (!field || value === undefined) return;
    if (value === '' && next[field.key] === undefined) return;
    next[field.key] = value;
  };
  const published = (field: Field | undefined, value: string | undefined) => (field && value !== undefined && field.values.includes(value) ? value : undefined);
  if (view.pick) put(f.outputs, comboOf(f.outputs, view.pick));
  if (view.nph !== undefined) put(f.count, published(f.count, String(view.nph)));
  put(f.platform, published(f.platform, view.plat));
  if (f.text) put(f.text, view.text);
  else if (f.name) {
    const parts = splitProductText(view.text);
    put(f.name, parts.name);
    put(f.advantages, parts.advantages.join('\n'));
  }
  if (view.wish !== undefined) put(f.wishes, view.wish);
  for (const work of WORKS) {
    const value = view.vars[work];
    put(f.variants[work], published(f.variants[work], value === undefined ? undefined : String(value)));
  }
  return next;
}

/** Вход заказа → вид S без формы (заявка до прихода формы, хвост после перезагрузки): поля по допустимым именам ключей (product-model KEYS) */
export function viewOfInputs(form: StudioForm | null | undefined, inputs: Record<string, unknown>): McardView {
  const strings = Object.fromEntries(Object.entries(inputs).filter((entry): entry is [string, string] => typeof entry[1] === 'string'));
  if (form) return viewOf(form, strings);
  const first = (keys: string[]) => keys.map(key => strings[key]).find(value => value !== undefined);
  const name = first(['name']);
  const text = first(['text']) ?? (name !== undefined ? joinProductText(name, (first(['advantages']) ?? '').split('\n')) : '');
  const view: McardView = { text, vars: {} };
  const works = worksOf(first(['outputs', 'pick']));
  if (works) view.pick = pickOf(works);
  const n = Number(first(['photo_count', 'count']));
  if (Number.isSafeInteger(n) && n > 0) view.nph = n;
  const plat = first(['platform', 'market']);
  if (plat) view.plat = plat;
  const wish = first(['wishes']);
  if (wish !== undefined) view.wish = wish;
  const keys: Record<McardWork, string[]> = {
    photo: ['photo_set', 'set_id', 'set'],
    slides: ['slides_style'],
    video: ['video_motion', 'motion_id', 'motion'],
    desc: ['desc_tone', 'text_tone'],
  };
  for (const work of WORKS) {
    const value = first(keys[work]);
    if (value) view.vars[work] = varValue(work, value);
  }
  return view;
}
